import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import ReactSlider from 'react-slider';
import VideoPlayer from '../VideoPlayer';

// CSS styles for custom video seeker
const videoSeekerStyles = `
  .custom-video-seeker {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: transparent;
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
  }
  .custom-video-seeker::-webkit-slider-thumb {
    -webkit-appearance: none !important;
    appearance: none !important;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: hsl(var(--primary)) !important;
    cursor: pointer;
    border: 2px solid #ffffff !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: box-shadow 0.2s;
  }
  .custom-video-seeker::-webkit-slider-thumb:hover {
    box-shadow: 0 2px 6px hsl(var(--primary) / 0.4) !important;
    background: hsl(var(--primary)) !important;
  }
  .custom-video-seeker::-webkit-slider-thumb:active {
    box-shadow: 0 2px 8px hsl(var(--primary) / 0.5) !important;
    background: hsl(var(--primary)) !important;
  }
  .custom-video-seeker::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: hsl(var(--primary)) !important;
    cursor: pointer;
    border: 2px solid #ffffff !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: box-shadow 0.2s;
  }
  .custom-video-seeker::-moz-range-thumb:hover {
    box-shadow: 0 2px 6px hsl(var(--primary) / 0.4) !important;
    background: hsl(var(--primary)) !important;
  }
  .custom-video-seeker::-moz-range-thumb:active {
    box-shadow: 0 2px 8px hsl(var(--primary) / 0.5) !important;
    background: hsl(var(--primary)) !important;
  }
  .custom-video-seeker::-webkit-slider-runnable-track {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: transparent;
  }
  .custom-video-seeker::-moz-range-track {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: #334155;
  }
`;

// Inject styles on component mount
if (typeof document !== 'undefined') {
  const styleId = 'video-trimmer-editor-seeker-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = videoSeekerStyles;
    document.head.appendChild(style);
  }
}

interface VideoTrimmerEditorProps {
  open: boolean;
  onClose: () => void;
  onTrimComplete: (startTime: number, endTime: number) => void;
  videoUrl: string;
  videoDuration: number;
  initialStartTime?: number;
  initialEndTime?: number;
  needsMobileFormat?: boolean;
}

export default function VideoTrimmerEditor({ 
  open, 
  onClose, 
  onTrimComplete, 
  videoUrl, 
  videoDuration,
  initialStartTime = 0,
  initialEndTime,
  needsMobileFormat = false,
}: VideoTrimmerEditorProps): JSX.Element {
  const [startTime, setStartTime] = useState<number>(initialStartTime);
  const [endTime, setEndTime] = useState<number>(initialEndTime ?? videoDuration);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [crop, setCrop] = useState<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState<number | null>(1);
  
  const loadCropAndZoom = useCallback(() => {
    const savedCropData = localStorage.getItem('videoCropData');
    if (savedCropData) {
      try {
        const parsed = JSON.parse(savedCropData);
        if (parsed.crop && parsed.zoom) {
          setCrop(parsed.crop);
          setZoom(parsed.zoom);
        } else if (parsed.zoom) {
          setZoom(parsed.zoom);
        }
      } catch (e) {
        console.warn('Error loading saved crop data:', e);
      }
    }
  }, []);

  useEffect(() => {
    loadCropAndZoom();
  }, [open, videoUrl, loadCropAndZoom]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'videoCropData') loadCropAndZoom();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('videoCropDataUpdated', loadCropAndZoom);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('videoCropDataUpdated', loadCropAndZoom);
    };
  }, [loadCropAndZoom]);

  useEffect(() => {
    if (open && videoDuration > 0) {
      setStartTime(initialStartTime);
      setEndTime(initialEndTime ?? videoDuration);
      setCurrentTime(initialStartTime);
    }
  }, [open, videoDuration, initialStartTime, initialEndTime]);

  useEffect(() => {
    if (isPlaying && currentTime >= endTime) {
      setIsPlaying(false);
      setCurrentTime(startTime);
    }
  }, [isPlaying, currentTime, startTime, endTime]);

  const handleSliderChange = useCallback((value: number | number[]) => {
    if (Array.isArray(value) && value.length === 2) {
      const [newStart, newEnd] = value;
      setStartTime(Math.max(0, Math.min(newStart, videoDuration)));
      setEndTime(Math.max(0, Math.min(newEnd, videoDuration)));
    }
  }, [videoDuration]);

  const handleSliderAfterChange = useCallback((value: number | number[]) => {
    if (Array.isArray(value) && value.length === 2) {
      const [newStart, newEnd] = value;
      const finalStart = Math.max(0, Math.min(newStart, videoDuration));
      const finalEnd = Math.max(0, Math.min(newEnd, videoDuration));
      
      if (finalStart >= finalEnd) {
        if (finalStart > 0) {
          setStartTime(finalStart - 0.1);
          setEndTime(finalStart);
        } else {
          setStartTime(0);
          setEndTime(0.1);
        }
      } else {
        setStartTime(finalStart);
        setEndTime(finalEnd);
      }
      setCurrentTime(finalStart);
    }
  }, [videoDuration]);

  const handleSeek = useCallback((time: number) => {
    const clampedTime = Math.max(startTime, Math.min(time, endTime));
    setCurrentTime(clampedTime);
  }, [startTime, endTime]);

  const handleApply = useCallback(() => {
    const finalStart = Math.max(0, Math.min(startTime, videoDuration));
    const finalEnd = Math.max(0, Math.min(endTime, videoDuration));
    if (finalStart < finalEnd) {
      onTrimComplete(finalStart, finalEnd);
      onClose();
    }
  }, [startTime, endTime, videoDuration, onTrimComplete, onClose]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-muted border-border text-foreground p-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl">Trim Video</DialogTitle>
        </DialogHeader>
        
        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          <p className="text-muted-foreground text-sm">
            Select the start and end time for the video
          </p>
          
          <div className="relative aspect-video w-full bg-black rounded-lg overflow-hidden border border-border">
            {videoUrl ? (
              <VideoPlayer
                videoUrl={videoUrl}
                videoDuration={videoDuration}
                enableTrimming={false}
                startTime={startTime}
                endTime={endTime}
                isPlaying={isPlaying}
                onPlayingChange={setIsPlaying}
                currentTime={currentTime}
                onCurrentTimeChange={setCurrentTime}
                onSeek={handleSeek}
                showSlider={false}
                showTrimmer={false}
                crop={crop}
                zoom={zoom}
                needsMobileFormat={needsMobileFormat}
                showStoryOverlay={true}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <span className="text-muted-foreground">No video URL provided</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-2">
            <div className="space-y-1">
              <span className="text-[10px] uppercase font-bold text-muted-foreground">Start</span>
              <p className="text-sm font-mono text-foreground">{formatTime(startTime)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase font-bold text-muted-foreground">Current</span>
              <p className="text-sm font-mono text-primary">{formatTime(currentTime)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase font-bold text-muted-foreground">End</span>
              <p className="text-sm font-mono text-foreground">{formatTime(endTime)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase font-bold text-muted-foreground">Duration</span>
              <p className="text-sm font-mono text-emerald-400">{formatTime(endTime - startTime)}</p>
            </div>
          </div>

          <div className="space-y-6 px-2 pb-4">
            <div className="relative h-4 flex items-center">
              <div className="absolute inset-0 h-1.5 bg-card rounded-full top-1/2 -translate-y-1/2" />
              <div 
                className="absolute h-1.5 bg-primary rounded-full top-1/2 -translate-y-1/2"
                style={{ width: `${(currentTime / videoDuration) * 100}%` }}
              />
              <div 
                className="absolute h-1.5 bg-emerald-500/50 rounded-full top-1/2 -translate-y-1/2"
                style={{ 
                  left: `${(startTime / videoDuration) * 100}%`,
                  width: `${((endTime - startTime) / videoDuration) * 100}%`
                }}
              />
              <input
                type="range"
                min={0}
                max={videoDuration}
                step={0.1}
                value={currentTime}
                onChange={(e) => handleSeek(parseFloat(e.target.value))}
                className="custom-video-seeker absolute inset-0 z-10"
              />
            </div>

            <ReactSlider
              className="horizontal-slider h-6 flex items-center"
              thumbClassName="w-4 h-4 bg-primary border-2 border-white rounded-full cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-slate-900"
              trackClassName="slider-track h-1.5 bg-card rounded-full"
              min={0}
              max={videoDuration}
              step={0.1}
              value={[startTime, endTime]}
              onChange={handleSliderChange}
              onAfterChange={handleSliderAfterChange}
            />
          </div>
        </div>

        <DialogFooter className="p-6 border-t border-border bg-muted/50 gap-3">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground hover:bg-card">
            Cancel
          </Button>
          <Button onClick={handleApply} className="bg-primary hover:bg-primary/90 text-white px-8">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
